<script setup>
import { watch, ref, reactive, defineProps, defineEmits, onMounted } from "vue";
import { apiQuery } from "@/api/j24api";
import router from "@/router";
import DevicesList from "./DevicesList.vue";
import DeviceView from "./DeviceView.vue";
import BreadcrumbSeperator from "@/components/BreadcrumbSeperator.vue";
import { head } from "aws-amplify/api";

const props = defineProps({
  license: Object,
  dataType: String,
  data: Array,
});

const devices = reactive([]);

const emit = defineEmits(["Back"]);

watch(props.dataType, (newVal) => {
  console.log("Data type changed:", newVal);
  // Handle data type changes
  if (newVal) {
    getData();
  }
});

onMounted(() => {
  getData();
});

const loading = ref(false);
const headerIcon = ref("");
const headerText = ref("");

function convertDevicesData(data) {
  return data.map((device) => ({
    Computer_Name: device.computerName,
    Serial_Number: device.serial,
    Model: device.modelName,
    patchLastContact: device.lastContact,
    updatesAvailableCount: device.updatesAvailable?.length
      ? device.updatesAvailable.length
      : 0,
    configurationName: device.configurationName,
    ...device,
  }));
}

async function getAllDevices() {
  let result = await apiQuery("ELA", "patching/patchingManagement", {
    action: "getDevices",
    id: props.license._id,
    minimal: true,
  });
  if (result.length) {
    devices.push(...convertDevicesData(result));
  }
}

async function getDrillDownData() {
  try {
    let result = await apiQuery("ELA", "patching/patchingManagement", {
      action: "getPatchingDashboard",
      id: props.license._id,
      dataType: props.dataType,
    });
    if (result.result.length) {
      devices.push(...convertDevicesData(result.result));
    } else {
      console.log("No devices found for drill down data");
    }
  } catch (error) {
    console.error("Error fetching drill down data:", error);
  }
}

async function getData() {
  loading.value = true;
  switch (props.dataType) {
    case "devices":
      router.push({ name: "PatchingV2Devices" });
      break;
    case "poorBatteryHealth":
      breadCrumbName.value = "Poor Battery Health";
      headerIcon.value = "mdi-battery-alert";
      headerText.value =
        "Devices listed below have less than 80% battery health";
      await getDrillDownData();
      break;
    case "notEncrypted":
      breadCrumbName.value = "Not Encrypted";
      headerIcon.value = "mdi-lock-open-variant-outline";
      headerText.value = "Devices listed below are not encrypted";
      await getDrillDownData();
      break;
    case "lowStorage":
      breadCrumbName.value = "Low Storage";
      headerIcon.value = "mdi-harddisk";
      headerText.value = "Devices listed below have less than 10GB free space";
      await getDrillDownData();
      break;
    case "smartFailing":
      breadCrumbName.value = "SMART Status Failing";
      headerIcon.value = "mdi-harddisk";
      headerText.value =
        "Devices listed below have SMART status reporting as failing";
      await getDrillDownData();
      break;
    default:
      console.log("Unknown data type", props.dataType);
  }

  loading.value = false;
}

const selectedTab = ref("list");
const selectedDevice = ref(null);
const breadCrumbName = ref("");

function selectDevice(selected) {
  selectedDevice.value = selected;
  selectedTab.value = "device";
}

const randomKeys = ref({
  deviceView: 0,
});
function updateRandom(key) {
  randomKeys.value[key] = Math.floor(Math.random() * 100);
}
</script>

<template>
  <v-window v-model="selectedTab">
    <v-window-item value="list">
      <div style="display: flex; align-items: center" class="mb-2">
        <BreadcrumbEmit label="Dashboard" @click="$emit('Back')" />
        <BreadcrumbSeperator />
        <BreadcrumbEmit :label="breadCrumbName" notLink />
      </div>

      <h1 class="ma-1">{{ breadCrumbName }}</h1>
      <v-sheet class="ma-1 pt-2" rounded="lg" elevation="1">
        <v-row>
          <v-col cols="auto" class="pl-10">
            <v-btn color="yellow" :icon="headerIcon" readonly></v-btn>
          </v-col>
          <v-col alignSelf="center">
            <div>{{ headerText }}</div>
          </v-col>
        </v-row>
      </v-sheet>

      <DevicesList
        :devices="devices"
        :loading="loading"
        @view="selectDevice"
        hideRefresh
      />
    </v-window-item>

    <v-window-item value="device">
      <div style="display: flex; align-items: center" class="mb-2">
        <BreadcrumbEmit label="Dashboard" @click="$emit('Back')" />
        <BreadcrumbSeperator />

        <BreadcrumbEmit
          :label="breadCrumbName"
          @click="
            selectedTab = 'list';
            updateRandom('deviceView');
          "
        />
        <BreadcrumbSeperator />
        <BreadcrumbEmit :label="selectedDevice.Computer_Name" notLink />
      </div>
      <DeviceView
        :device="selectedDevice"
        :key="randomKeys.deviceView"
        :licenseId="props.license._id"
      />
    </v-window-item>
  </v-window>
</template>
